export const getFormSetBlockDocumentationMarkdown = `
### What does this block do?

The Get Form Set Block attempts to retrieve a form set based on a given configuration. It can fetch a form set using a specified Form Set ID when defined by the user, dynamically retrieve the most recent form set associated with a particular entity type if set to "Automatically Applied," or retrieve multiple form sets if set to "Automatically Applied All." Additional optional filters like Form Template ID and Primitive can further refine the retrieval. This enables administrators to manage and apply patient forms through workflow rules or manual selection.

### When to use it?

- Use this block when you need to fetch a specific set of forms that will be applied to a patient or entity as part of a workflow.  
- Use "User Defined" mode when the form set is predetermined by its ID.  
- Use "Automatically Applied" mode to dynamically retrieve the most recent form set based on the entity type in question.
- Use "Automatically Applied All" mode to retrieve multiple form sets for an entity, optionally filtered by creation date range and result limit.
- Employ it in workflows where different filtering criteria (like a specific form template or primitive type) help determine the correct form set to apply during patient intake or follow-up processes.

### Example Flows

1. **Automated Pre-Visit Forms:**  
  In a pre-visit workflow, the system is set to "Automatically Applied." The block retrieves the most recent form set associated with the patient's entity type (for example, "Patient"). Optional filters such as Form Template ID help ensure that the right pre-visit questionnaire is applied automatically.

2. **User-Defined Consent Form Set:**  
  When a specialized consent process is required, an administrator might select "User Defined" mode and manually input a specific Form Set ID. This ensures that a predetermined set of forms, perhaps tailored for a certain treatment plan, is sent to the patient.

3. **Custom Screening Forms:**  
  In a population health screening workflow, the block can be configured to retrieve form sets by specifying an optional Primitive value along with the Form Template ID. This allows the system to filter and apply forms that are designed for specific screening criteria or assessments.

4. **Historical Form Set Review:**  
  In an audit or reconciliation workflow, the block is set to "Automatically Applied All" with After and Before dates to retrieve all form sets created within a specific window. A Limit can cap how many results are returned when only the most recent records are needed.

## How to Configure This Block

### **1. Select the Type**  
- **Description:** In the configuration tab, locate the Type dropdown menu.  
- **Options:**  
  - **User Defined:** The form set ID will be provided directly by the user.  
  - **Automatically Applied:** The system dynamically retrieves the most recent form set based on the entity type. (Default: "Automatically Applied")  
  - **Automatically Applied All:** The system retrieves multiple form sets based on the entity type, sorted by creation date (newest first). Returns an array of form sets.

### **2. Enter Required Fields Based on Type**  
- **If using User Defined:**  
  - **Form Set ID:** Required. Enter the unique identifier of the form set to get.  

- **If using Automatically Applied or Automatically Applied All:**  
  - **Search Scope:** Defines where the system looks for form sets.
    - **Current Practice (Default):** Searches only within the practice associated with the entity that triggered the flow (e.g., the practice for a specific patient, or encounter).
    - **Across EHR System:** Searches all linked practices in your organization where the patient entity has records. Use this to find the most recent form set for a patient. Note that you will only be able to see form sets from practices belonging to organizations that your account has access to. **Important:** This option only applies when working with patient entities - for other entity types (encounters, orders, etc.), both search scope options will return the same results.

- **If using Automatically Applied All only:**  
  - **After:** (Optional) The start date (inclusive) for filtering form sets by creation date. Formatted YYYY-MM-DD as per ISO 8601. Supports \`{{...}}\` template notation. If not provided, no lower bound is applied.  
  - **Before:** (Optional) The end date (inclusive) for filtering form sets by creation date. Formatted YYYY-MM-DD as per ISO 8601. Supports \`{{...}}\` template notation. If not provided, no upper bound is applied.  
  - **Limit:** (Optional) Maximum number of form sets to return (1–50). Defaults to 10. Form sets are ordered by creation date, newest first.

### **3. Additional Optional Filters**  
- **Form Template ID:**  
  - (Optional) Enter the ID of the form template to further filter the form set.  

- **Primitive:**  
  - (Optional) Specify the primitive value to use in filtering the form set.  

### **4. Mock Output for Test Run**  
- **Description:** Use the "Mock output for test run" toggle (default is Off) at the bottom.  
- **Helper Text:** "When enabled, the block will return preset data instead of querying live systems, which is useful for testing your workflow."

### **5. Save Your Configuration**  
After entering the required and optional fields, save your configuration to update the flow.

## How to Test

### **1. Set Up Test Data**  
- Ensure that your test environment has form sets available (either by creating them manually or via a form generation process).

### **2. Use Test Mode**  
- Toggle "Mock output for test run" to On to simulate responses without accessing live data.

### **3. Run the Flow**  
- Execute your flow and verify that the block returns the expected form set information based on the chosen configuration (User Defined, Automatically Applied, or Automatically Applied All).

### **4. Verify Filtering**  
- Check that if optional fields (Form Template ID, Primitive) are used, they correctly narrow down the returned form set(s).
- If "Across EHR System" is selected in Search Scope, verify that the block finds form sets from other practices where the patient has records.
- For "Automatically Applied All," verify that After, Before, and Limit filters return the expected number of form sets within the specified date range.

### **5. Review Output**  
- Confirm that the output contains valid form set record(s) along with identifiers or metadata that your downstream blocks require.
- For "Automatically Applied All," confirm the output is an array of form sets.

## Best Practices

1. **Choose the Correct Type:**  
  Decide between User Defined, Automatically Applied, and Automatically Applied All based on whether you need a fixed form set, the single most recent match, or multiple form sets.

2. **Provide All Required Information:**  
  - For User Defined mode, ensure that a valid Form Set ID is provided.  
  - For Automatically Applied and Automatically Applied All modes, the entity that triggered the flow determines which form sets are searched.

3. **Choose the Correct Search Scope:**  
  - For practice-specific workflows, use **Current Practice** to ensure you retrieve forms relevant only to the current location. This prevents accidentally using a form from another practice.
  - For centralized workflows or to accommodate patients who visit multiple locations, use **Across EHR System**. This ensures you always get the most recent form set for the patient, regardless of where it was created. When using this option, remember that you will only be able to see form sets from practices belonging to organizations that your account has access to. **Note:** This expanded search only applies to patient entities - for other entity types, both search scope options will produce identical results.

4. **Utilize Optional Filters:**  
  Use Form Template ID and Primitive filters to fine-tune which form set(s) are retrieved. For Automatically Applied All, use After and Before to scope results to a creation date range, and Limit to control how many form sets are returned.

5. **Test with Mock Mode:**  
  Use the "Mock output for test run" functionality during development to validate configuration without affecting live data.

6. **Document Your Configuration:**  
  Keep records of the form set IDs and the filtering criteria used, as this will aid in troubleshooting and future updates.

### Example Output

For **User Defined** and **Automatically Applied**, the block returns a single form set object:

~~~json
{
  "id": "{formSetId}",
  "forms": {
    "{formId}": {
      "id": "{formId}",
      "name": "Text",
      "score": null,
      "fields": [
        {
          "id": "{previsitFormFieldId}",
          "max": null,
          "min": null,
          "name": "text",
          "page": 1,
          "rank": 1,
          "label": "Text",
          "value": "",
          "prefix": null,
          "source": "INGEST",
          "suffix": null,
          "created": "2025-03-27T15:02:23.210Z",
          "deleted": false,
          "options": null,
          "updated": "2025-03-27T15:02:23.210Z",
          "interval": null,
          "lastPage": null,
          "required": false,
          "sublabel": null,
          "primitive": "TEXT_INPUT",
          "conditions": [],
          "helpDialog": null,
          "dataBinding": null,
          "placeholder": null,
          "defaultValue": "",
          "customConfigs": {},
          "characterLimit": null,
          "previsitFormId": "{formId}",
          "fhirTotalResult": null,
          "helpDialogLabel": null,
          "optionsMetadata": null,
          "deletedTimestamp": null,
          "previsitFormSetId": "{previsitFormSetId}",
          "continueButtonMode": null,
          "hideContinueButton": false,
          "fhirAppointmentKinds": null,
          "fhirDynamicProviders": null,
          "previsitFormTemplateId": "{previsitFormTemplateId}",
          "scheduleAppointmentSortBy": null,
          "scheduleAppointmentEndTime": null,
          "scheduleAppointmentDisplayBy": null,
          "scheduleAppointmentStartTime": null,
          "previsitFormTemplateFieldConfigId": "{previsitFormTemplateFieldConfigId}",
          "registrationAdditionalLoginInputs": null,
          "registrationFhirEhrReconciliation": null,
          "scheduleAppointmentCommentEnabled": false,
          "hidePatientCareProvidersIfPopulated": false,
          "registrationEhrProviderInformationId": null,
          "registrationEnableContactVerification": null,
          "scheduleAppointmentCallSectionEnabled": false,
          "scheduleAppointmentDefaultReasonForVisit": null
        }
      ],
      "labels": [
        "Test Label"
      ],
      "scores": null,
      "status": "CREATED",
      "context": null,
      "displayName": "Text",
      "pageConfigs": {},
      "exportAsForm": false,
      "isReviewable": null,
      "recommendation": null,
      "hideWhenCompleted": false,
      "previsitFormSetId": "{previsitFormSetId}",
      "templateIsReviewable": false,
      "previsitFormTemplateId": "{previsitFormTemplateId}"
    }
  },
  "status": "CREATED",
  "created": "2025-03-27T15:02:23.181Z",
  "expires": "2025-03-27T16:02:00.000Z",
  "updated": "2025-03-27T15:02:23.181Z",
  "completed": null,
  "patientId": "{patientId}",
  "practiceId": "{practiceId}",
  "encounterId": "{encounterId}",
  "patientEhrId": "{patientEhrId}",
  "authBypassCode": "{authBypassCode}",
  "previsitLinkId": null,
  "authCodeExpires": "2025-03-30T15:02:23.180Z",
  "closedTimestamp": null,
  "ehrInformationId": "{ehrInformationId}",
  "currentStateExported": false
}
~~~

For **Automatically Applied All**, the block returns an array of form set objects (each with the same shape as above), ordered by creation date with the newest first.

`;
